import React, { useEffect, useState } from 'react';
import { IPublicClientApplication } from '@azure/msal-browser';
import { Task } from '../types';
import {
  CustomField, CustomFieldValue, CustomFieldType,
  listProjectCustomFields, getTaskCustomFieldValues,
  upsertTaskCustomFieldValue, fieldOptions, readValue,
} from '../services/customFieldService';

// Task-drawer inputs for the project's custom fields. Values commit on
// blur / select change (matching the drawer's auto-save pattern) via EAV
// upsert; drafts live locally so typing never fires a PATCH per keystroke.
export default function CustomFieldsPane({ pca, task }: { pca: IPublicClientApplication; task: Task }) {
  const [fields, setFields] = useState<CustomField[] | null>(null);
  const [values, setValues] = useState<Map<string, CustomFieldValue>>(new Map());
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const pid = task._dagrun_pm_projectid_value;
    if (!pid) { setFields([]); return; }
    Promise.all([
      listProjectCustomFields(pca, pid),
      getTaskCustomFieldValues(pca, task.dagrun_pm_taskid),
    ]).then(([f, v]) => {
      if (cancelled) return;
      const byField = new Map<string, CustomFieldValue>();
      v.forEach(row => byField.set(row._dagrun_pm_fieldid_value, row));
      setFields(f);
      setValues(byField);
      const d: Record<string, string> = {};
      f.forEach(fd => { d[fd.dagrun_pm_customfieldid] = readValue(fd, byField.get(fd.dagrun_pm_customfieldid)); });
      setDrafts(d);
    }).catch(e => {
      if (!cancelled) { setFields([]); setError(e instanceof Error ? e.message : 'Failed to load custom fields'); }
    });
    return () => { cancelled = true; };
  }, [pca, task.dagrun_pm_taskid, task._dagrun_pm_projectid_value]);

  const commit = async (field: CustomField, raw: string) => {
    const existing = values.get(field.dagrun_pm_customfieldid);
    const current = readValue(field, existing);
    if (raw === current) return;
    try {
      const id = await upsertTaskCustomFieldValue(pca, task.dagrun_pm_taskid, field, existing, raw);
      setValues(prev => {
        const next = new Map(prev);
        next.set(field.dagrun_pm_customfieldid, {
          ...(existing ?? {
            dagrun_pm_customfieldvalueid: id,
            _dagrun_pm_fieldid_value: field.dagrun_pm_customfieldid,
            _dagrun_pm_taskid_value: task.dagrun_pm_taskid,
          }),
          dagrun_pm_customfieldvalueid: id,
          [field.dagrun_pm_fieldtype === CustomFieldType.Decimal ? 'dagrun_pm_valuenumber'
            : field.dagrun_pm_fieldtype === CustomFieldType.Date ? 'dagrun_pm_valuedate'
            : field.dagrun_pm_fieldtype === CustomFieldType.ChoiceSingle ? 'dagrun_pm_valuechoice'
            : 'dagrun_pm_valuetext']: raw.trim() === '' ? null
            : field.dagrun_pm_fieldtype === CustomFieldType.Decimal ? Number(raw) : raw.trim(),
        } as CustomFieldValue);
        return next;
      });
      setError(null);
    } catch (e) {
      // Snap the draft back to the last persisted value.
      setDrafts(d => ({ ...d, [field.dagrun_pm_customfieldid]: current }));
      setError(e instanceof Error ? e.message : 'Save failed');
    }
  };

  if (fields === null) return <div style={{ fontSize: 12, color: 'var(--ink-4)' }}>Loading…</div>;
  if (fields.length === 0) {
    return (
      <div style={{ fontSize: 12, color: 'var(--ink-4)' }}>
        No custom fields defined for this project. PMs can add them in Settings → Custom Fields.
      </div>
    );
  }

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
      {fields.map(f => {
        const draft = drafts[f.dagrun_pm_customfieldid] ?? '';
        const setDraft = (v: string) => setDrafts(d => ({ ...d, [f.dagrun_pm_customfieldid]: v }));
        const type = f.dagrun_pm_fieldtype;
        return (
          <div key={f.dagrun_pm_customfieldid}>
            <label>{f.dagrun_pm_name}</label>
            {type === CustomFieldType.ChoiceSingle ? (
              <select
                value={draft}
                onChange={e => { setDraft(e.target.value); commit(f, e.target.value); }}
              >
                <option value="">—</option>
                {fieldOptions(f).map(o => <option key={o} value={o}>{o}</option>)}
                {/* A stored value no longer in the option list stays selectable */}
                {draft && !fieldOptions(f).includes(draft) && <option value={draft}>{draft}</option>}
              </select>
            ) : type === CustomFieldType.Date ? (
              <input type="date" value={draft}
                onChange={e => { setDraft(e.target.value); commit(f, e.target.value); }} />
            ) : type === CustomFieldType.Decimal ? (
              <input type="number" value={draft}
                onChange={e => setDraft(e.target.value)}
                onBlur={e => commit(f, e.target.value)} />
            ) : (
              <input type="text" value={draft}
                onChange={e => setDraft(e.target.value)}
                onBlur={e => commit(f, e.target.value)} />
            )}
          </div>
        );
      })}
      {error && <div style={{ gridColumn: '1 / span 2', fontSize: 11, color: 'var(--danger)' }}>{error}</div>}
    </div>
  );
}
